<template>
  <div class="lane-metrics" :class="{ 'is-compact': compact }" :aria-label="label ? `${label}队列运行状态` : '队列运行状态'">
    <div class="lane-pressure-heading">
      <div class="lane-pressure-title">
        <b v-if="label">{{ label }}</b>
        <strong>待处理 {{ waiting }}</strong>
      </div>
      <div class="lane-pressure-status">
        <el-tag size="mini" :type="pressureTag">{{ pressureText }}</el-tag>
        <span>积压压力 {{ pressurePercentage }}% · 每 Worker {{ perWorker }}</span>
      </div>
    </div>
    <el-progress
      class="lane-pressure-progress"
      :percentage="pressurePercentage"
      :status="progressStatus"
      :stroke-width="10"
      :show-text="false"
    />
    <div class="pressure-caption">队列压力：每个生效 Worker 对应 {{ perWorker }} 个未领取任务；这不是任务完成百分比</div>
    <div class="lane-core-grid">
      <div><span>等待</span><strong>{{ waiting }}</strong></div>
      <div><span>执行中</span><strong>{{ value.processing || 0 }}</strong></div>
      <div><span>延迟重试</span><strong>{{ value.delayed_retry || 0 }}</strong></div>
      <div><span>主数据阻塞</span><strong>{{ value.blocked_master || 0 }}</strong></div>
    </div>
    <div class="worker-heading"><strong>Worker 运行状态</strong><span>利用率 {{ workerState.utilization }}%</span></div>
    <el-progress class="worker-progress" :percentage="workerState.utilization" :stroke-width="8" :show-text="false" />
    <div class="worker-grid">
      <div><span>页面配置</span><strong>{{ workerState.configured }}</strong></div>
      <div :class="{ mismatch: workerState.configMismatch }"><span>Go 已生效</span><strong>{{ workerState.effective }}</strong></div>
      <div><span>在线槽位</span><strong>{{ workerState.online }}</strong></div>
      <div><span>忙碌</span><strong>{{ workerState.busy }}</strong></div>
      <div><span>空闲</span><strong>{{ workerState.idle }}</strong></div>
    </div>
    <div class="lane-diagnosis" :class="`is-${diagnosis.level}`">
      <strong>{{ diagnosis.title }}</strong><span>{{ diagnosis.detail }}</span>
    </div>
    <div class="lane-details" :class="{ 'is-compact': compact }">
      <span>自然转换待执行 {{ value.scheduled_transitions || 0 }} · 最老等待 {{ formatDuration(value.oldest_wait_seconds) }}</span>
      <span :class="heartbeatClass">Worker 心跳：{{ heartbeatText }}</span>
      <template v-if="!compact">
        <span>本分钟 入队/消费 {{ value.enqueued_minute || 0 }}/{{ value.consumed_minute || 0 }} · 成功/失败 {{ value.success_minute || 0 }}/{{ value.failure_minute || 0 }}</span>
        <span>累计 入队/消费 {{ value.enqueued_total || 0 }}/{{ value.consumed_total || 0 }} · 成功/失败 {{ value.success_total || 0 }}/{{ value.failure_total || 0 }}</span>
        <span>任务 {{ value.last_task_duration_ms || 0 }} ms · 构建 {{ value.last_build_duration_ms || 0 }} ms · 请求 {{ formatBytes(value.last_payload_bytes) }}</span>
        <span>库存 {{ value.last_inventory_count || 0 }} · 价格 {{ value.last_rate_count || 0 }} · 堆内存 {{ formatBytes(value.go_heap_inuse_bytes) }}</span>
        <span>GC {{ value.go_gc_count || 0 }} · Goroutine {{ value.go_goroutines || 0 }} · GOMEMLIMIT {{ value.go_memory_limit_bytes ? formatBytes(value.go_memory_limit_bytes) : '未配置' }}</span>
        <span>最后消费 {{ value.last_consume_at || '-' }}</span>
        <span>最后成功 {{ value.last_success_at || '-' }}</span>
        <span>最后失败 {{ value.last_failure_at || '-' }}</span>
        <el-tooltip v-if="value.last_failure_summary" :content="value.last_failure_summary" placement="top">
          <span class="failure">失败摘要：{{ value.last_failure_summary }}</span>
        </el-tooltip>
      </template>
    </div>
  </div>
</template>

<script>
import { jielvLaneBacklogPerWorker, jielvLaneDiagnosis, jielvLanePressurePercentage, jielvLanePressureStatus, jielvLanePressureText, jielvLaneWorkerState } from '../workerControlMeta'

const emptyMetric = () => ({
  ready: 0,
  waiting: 0,
  processing: 0,
  delayed_retry: 0,
  scheduled_transitions: 0,
  oldest_wait_seconds: 0,
  actual_workers: 0,
  configured_workers: 0,
  worker_processes: 0,
  blocked_master: 0,
  heartbeat_stale: true,
})

export default {
  name: 'JielvLaneMetrics',
  props: {
    label: { type: String, default: '' },
    value: { type: Object, default: emptyMetric },
    configuredWorkers: { type: Number, default: 0 },
    compact: { type: Boolean, default: false },
  },
  computed: {
    waiting() { return Math.max(0, Number(this.value.waiting == null ? this.value.ready : this.value.waiting) || 0) },
    pressurePercentage() { return jielvLanePressurePercentage(this.value, this.configuredWorkers) },
    pressureText() { return jielvLanePressureText(this.value, this.configuredWorkers) },
    pressureTag() { return { success: 'success', warning: 'warning', exception: 'danger' }[jielvLanePressureStatus(this.value, this.configuredWorkers)] || 'info' },
    progressStatus() { return jielvLanePressureStatus(this.value, this.configuredWorkers) },
    perWorker() { return jielvLaneBacklogPerWorker(this.value, this.configuredWorkers).toFixed(1) },
    workerState() { return jielvLaneWorkerState(this.value, this.configuredWorkers) },
    diagnosis() { return jielvLaneDiagnosis(this.value, this.configuredWorkers) },
    heartbeatClass() {
      if (!(this.value.heartbeat_stale || this.value.worker_heartbeat_stale)) return 'heartbeat-ok'
      return this.waiting + Number(this.value.processing || 0) + Number(this.value.delayed_retry || 0) > 0 ? 'heartbeat-stale' : 'heartbeat-idle'
    },
    heartbeatText() {
      if (this.value.heartbeat_stale || this.value.worker_heartbeat_stale) {
        if (this.waiting + Number(this.value.processing || 0) + Number(this.value.delayed_retry || 0) === 0) return `空闲（最后 ${this.value.workers_observed_at || '无记录'}）`
        return `已过期（最后 ${this.value.workers_observed_at || '无记录'}）`
      }
      const age = this.value.heartbeat_age == null ? this.value.worker_heartbeat_age_seconds : this.value.heartbeat_age
      return `正常（${this.value.workers_observed_at || '刚刚'}，${this.formatDuration(age)}前）`
    },
  },
  methods: {
    formatDuration(value) {
      const seconds = Math.max(0, Number(value || 0))
      if (seconds < 60) return `${seconds}秒`
      if (seconds < 3600) return `${Math.floor(seconds / 60)}分钟`
      return `${Math.floor(seconds / 3600)}小时${Math.floor((seconds % 3600) / 60)}分钟`
    },
    formatBytes(value) {
      const bytes = Number(value || 0)
      if (bytes <= 0) return '-'
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
      return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
    },
  },
}
</script>

<style lang="scss" scoped>
.lane-metrics { display: flex; flex-direction: column; gap: 7px; min-width: 0; color: #606266; font-size: 12px; }
.lane-pressure-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.lane-pressure-title { display: flex; align-items: baseline; gap: 8px; min-width: 0; }
.lane-pressure-title b { color: #606266; font-size: 13px; }.lane-pressure-title strong { color: #303133; font-size: 15px; }
.lane-pressure-status { display: flex; align-items: center; gap: 7px; color: #909399; font-size: 11px; white-space: nowrap; }
.lane-pressure-progress { width: 100%; margin: 0 0 2px; }
.lane-pressure-progress::v-deep .el-progress-bar__outer { background: #dfe7ef; }
.pressure-caption { margin-top: -2px; color: #909399; font-size: 10px; }
.lane-core-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); overflow: hidden; border: 1px solid #e4e7ed; border-radius: 5px; background: #e4e7ed; gap: 1px; }
.lane-core-grid div { display: flex; min-width: 0; padding: 7px 8px; background: #fff; flex-direction: column; gap: 2px; }
.lane-core-grid span { overflow: hidden; color: #909399; font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }.lane-core-grid strong { color: #303133; font-size: 16px; }
.worker-heading { display: flex; align-items: center; justify-content: space-between; margin-top: 3px; color: #606266; font-size: 11px; }
.worker-heading span { color: #909399; }.worker-progress { margin-top: -2px; }.worker-progress::v-deep .el-progress-bar__outer { background: #edf1f5; }
.worker-grid { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); overflow: hidden; border: 1px solid #e4e7ed; border-radius: 5px; background: #e4e7ed; gap: 1px; }
.worker-grid div { display: flex; min-width: 0; padding: 6px; background: #f8fafc; flex-direction: column; gap: 2px; text-align: center; }
.worker-grid span { overflow: hidden; color: #909399; font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }.worker-grid strong { color: #303133; font-size: 14px; }.worker-grid .mismatch { background: #fff7e6; }.worker-grid .mismatch strong { color: #e6a23c; }
.lane-diagnosis { display: flex; align-items: flex-start; gap: 8px; padding: 7px 9px; border-radius: 5px; font-size: 11px; }
.lane-diagnosis strong { flex: 0 0 auto; }.lane-diagnosis span { min-width: 0; overflow-wrap: anywhere; }.lane-diagnosis.is-success { color: #328857; background: #edf8f1; }.lane-diagnosis.is-warning { color: #9b6500; background: #fff7e6; }.lane-diagnosis.is-danger { color: #c53b3b; background: #fef0f0; }
.lane-details { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 3px 12px; }.lane-details span { min-width: 0; overflow-wrap: anywhere; }
.lane-details.is-compact { grid-template-columns: 1fr; }
.heartbeat-ok { color: #45a66b; }.heartbeat-idle { color: #909399; }.heartbeat-stale, .failure { color: #f56c6c; }.failure { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; cursor: help; }
.is-compact { gap: 5px; }.is-compact .lane-pressure-title b { color: #303133; }.is-compact .lane-details { color: #909399; font-size: 11px; }
@media (max-width: 640px) {
  .lane-pressure-heading { align-items: flex-start; flex-direction: column; gap: 5px; }
  .lane-pressure-status { white-space: normal; }
  .lane-core-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .worker-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .lane-details { grid-template-columns: 1fr; }
}
</style>
